/** Regras puras de quantidade de loot, compartilhadas pelo jogo e auditoria. */

export const DROP_SELL_VALUE_MULTIPLIER = 1.2;

// Release baseline for the level-150 hunt. These are audit targets, not a
// hidden multiplier: the analyzer and the balance test use the same values so
// a future economy change is visible to players and reviewers.
export const ECONOMY_TARGETS = Object.freeze({
  level: 150,
  killsPerHour: 1200,
  goldPerHourTarget: 600000,
  goldPerHourMin: 500000,
  goldPerHourMax: 850000,
});

export function dropBudgetForLevel(level = 1, stage = 0) {
  const safeLevel = Math.max(1, Number(level) || 1);
  const safeStage = Math.max(0, Math.min(3, Number(stage) || 0));
  return Math.round((45 + safeLevel * 2.4) * (1 + safeStage * 0.16) * DROP_SELL_VALUE_MULTIPLIER);
}

export function primaryDropChanceForLevel(level = 1) {
  return Math.min(0.85, 0.65 + Math.max(1, Number(level) || 1) / 1500);
}

export function stoneDropChanceForLevel(level = 1) {
  return 0.003 + Math.min(0.009, Math.max(1, Number(level) || 1) / 300 * 0.009);
}

export function signatureDropChanceForStage(stage = 0) {
  return 0.12 + Math.max(0, Math.min(3, Number(stage) || 0)) * 0.07;
}

export function evolutionCoinCost(level = 1, evolutionIndex = 0) {
  const safeLevel = Math.max(1, Math.floor(Number(level) || 1));
  const stageMultiplier = 1 + Math.max(0, Math.min(2, Number(evolutionIndex) || 0)) * 0.35;
  return Math.max(1000, Math.round(safeLevel * safeLevel * 20 * stageMultiplier / 100) * 100);
}

export function budgetedQuantity(unitValue, budget, random = Math.random) {
  const safeUnitValue = Math.max(1, Number(unitValue) || 1);
  // Quando uma unidade vale mais que o budget, o arredondamento pode retornar
  // zero. Isso preserva o valor esperado e impede drops caros de dominarem as
  // rotas iniciais apenas porque a quantidade mínima era forçada para um.
  const exact = Math.max(0, (Number(budget) || 0) / safeUnitValue);
  const whole = Math.floor(exact);
  const fraction = exact - whole;
  return whole + (random() < fraction ? 1 : 0);
}

/** Chance Poke Idle (base 100000) → probabilidade 0–1. */
export function lootChanceToProbability(chance = 0) {
  return Math.max(0, Math.min(1, (Number(chance) || 0) / 100000));
}

/**
 * Escala a faixa de quantidade da tabela pelo nível do wild.
 * Tabelas do Poke Idle já embutem poder da espécie; no idlemon o mesmo mon
 * aparece em vários níveis, então o max cresce suavemente com o nível.
 */
export function scaledLootQuantity(minCount = 1, maxCount = 1, level = 1, random = Math.random) {
  const min = Math.max(1, Math.round(Number(minCount) || 1));
  const max = Math.max(min, Math.round(Number(maxCount) || min));
  const safeLevel = Math.max(1, Number(level) || 1);
  const factor = Math.min(1.35, 0.4 + safeLevel / 180);
  const scaledMax = Math.max(min, Math.round(max * factor));
  return min + Math.floor(random() * (scaledMax - min + 1));
}

/**
 * Multiplicador para aproximar o EV de loot estável (sem pedras) do dropBudget.
 * Pedras ficam de fora do EV — continuam jackpot raro.
 * Tabelas com fillers baratos (sell 1–5) precisam de scale alto para bater o budget.
 */
export function speciesLootQtyScale(stableExpectedValue = 0, budget = 1) {
  const ev = Math.max(0.01, Number(stableExpectedValue) || 0);
  const safeBudget = Math.max(1, Number(budget) || 1);
  // Sem teto alto: fillers baratos precisam de scale grande para bater o budget.
  // O piso evita zerar tabelas ricas demais.
  return Math.max(0.05, safeBudget / ev);
}

/**
 * Fator de nível na venda de capturado. Espelha capturedLevelFactor em
 * pokemon_sell_price_server.
 *
 * A curva antiga era `1 + min(1.2, (level-1)*0.004)`: praticamente plana
 * (×1,02 no Nv.5, ×1,60 no Nv.150). Vender capturado deixava de ser renda por
 * volta do Nv.10 e sobrava só o drop — daí a queixa do beta de não conseguir
 * bancar pokébolas. Medido contra o Poke Idle (creatures.json), a venda deles
 * vai de ~200 no início a ~18.000 no Nv.100, enquanto a nossa ia de 508 a 698.
 *
 * Potência calibrada nos pontos deles: Nv.20 ≈ 2.800, Nv.40 ≈ 6.700,
 * Nv.100 ≈ 21.000. O teto evita renda infinita no endgame.
 */
export function capturedLevelFactor(level = 1) {
  const safeLevel = Math.max(1, Number(level) || 1);
  // Piso 1: a curva de potência só ultrapassa o valor antigo a partir do Nv.5.
  // Sem ele, capturado de Nv.1 valeria MENOS do que hoje — regressão logo no
  // onboarding, justamente onde o jogador precisa bancar as primeiras bolas.
  // Teto 40 (= 20.000 com a base 5.000) fica na altura do teto do Poke Idle,
  // ~18.000. Alcançado por volta do Nv.100 e plano depois, como o deles.
  return Math.max(1, Math.min(40, Math.pow(safeLevel / 5, 1.25)));
}

export function capturedPokemonSellPrice(pokemon, speciesBase = 5000) {
  const base = Math.max(1, Number(speciesBase) || 5000);
  const level = Math.max(1, Number(pokemon?.level || pokemon?.encounterLevel) || 1);
  const mult = Number(pokemon?.mult) || 1;
  const iv = Math.max(0, Math.min(100, Number(pokemon?.ivTotal) || 0));
  const capturedBase = base * 0.1;
  const levelBonus = capturedLevelFactor(level);
  const rarityBonus = 1 + Math.max(0, mult - 1) * 1.25;
  const ivBonus = 0.8 + iv / 100 * 0.4;
  const shinyBonus = pokemon?.shiny ? 2.5 : 1;
  return Math.max(25, Math.round(capturedBase * levelBonus * rarityBonus * ivBonus * shinyBonus));
}
